import { useSyncExternalStore } from 'react';
import { ApiError, DEFAULT_API, apiCall } from './sync';
import { currentUser } from './auth';
import { getLang } from '../i18n';

/**
 * Konto-Profil, Stufe und Branding (docs/21). Die Typen unten sind die Kopie
 * von `services/sync/src/protocol.ts` — beide Seiten halten sich an §2.
 *
 * Die App **rechnet nichts selbst**: Welche Stufe gerade gilt, welche Limits
 * und Merkmale daran hängen, sagt der Server in `plan.effektiv`, `plan.limits`
 * und `plan.features`. Hier liegt nur, was die Oberfläche braucht — das Konto
 * aus `GET /me`, gecacht in `localStorage`, damit es offline weitergilt, und
 * ein kleiner Store, damit Marke, Gates und Konto-Seite denselben Stand sehen.
 */
export type Stufe = 'frei' | 'standard' | 'pro';

export interface Profil {
  name?: string; // Anzeigename der Person, max 80
  verein?: string; // Verein / Verband / Gemeinde, max 80
  ort?: string; // max 80
  website?: string; // max 200, http(s)
  telefon?: string; // max 40 — nur für Rechnung/Support, nie öffentlich
  sprache?: 'de' | 'en' | 'it';
  /** vom Server gemerkt: Sprache der App beim letzten Kontakt — gilt für Mails, solange `sprache` leer ist */
  appSprache?: 'de' | 'en' | 'it';
  /** zuletzt bei einer Bestellung angegebene Rechnungsadresse — das Formular füllt sich damit vor */
  rechnungsadresse?: Rechnungsadresse;
}

/**
 * Rechnungsadresse (docs/notes/rechnung.md): Pflicht bei jeder Bestellung —
 * die Rechnung geht sofort mit der Bestätigung raus. Verein/Firma und Ort
 * Pflicht, Straße/PLZ freiwillig (Kleinbetragsrechnung), Land ISO-2.
 */
export interface Rechnungsadresse {
  firma: string;
  strasse?: string;
  plz?: string;
  ort: string;
  land: string;
  ustId?: string;
}

export type RechnungStatus = 'offen' | 'bezahlt' | 'storniert';

/** Eine Rechnung, wie sie die Admin-Seite zeigt (GET /admin/rechnungen) */
export interface Rechnung {
  nummer: string;
  datum: number;
  sub: string;
  email?: string;
  stufe: 'standard' | 'pro';
  betragEur: number;
  adresse: Rechnungsadresse;
  sprache: 'de' | 'en' | 'it';
  status: RechnungStatus;
  bezahltAm?: number;
  freigeschaltetBis?: number;
  notiz?: string;
}

export interface Plan {
  stufe: Stufe;
  /** Ende der Gültigkeit (ms UTC); fehlt bei `frei` */
  gueltigBis?: number;
  /** Testphase — dann ist `stufe` = 'pro' mit `gueltigBis` = Start + 30 Tage */
  test?: boolean;
  /** wann das Konto zum ersten Mal am Server war (ms) */
  seit: number;
  /** effektive Stufe unter Berücksichtigung von gueltigBis — vom Server berechnet */
  effektiv: Stufe;
  /** Limits der effektiven Stufe — vom Server berechnet, die App rechnet nichts selbst */
  limits: { helfer: number | null; mitorganisatoren: number | null }; // null = unbegrenzt
  /** Merkmale der effektiven Stufe */
  features: { branding: boolean; saison: boolean; excel: boolean; etiketten: boolean; cloud: boolean };
  /** Freischaltungen durch den Betreiber (docs/21 §5), jüngste zuletzt, max 20 */
  verlauf?: PlanEintrag[];
}

/** Ein Eintrag im Verlauf: wer hat wann welche Stufe bis wann gesetzt */
export interface PlanEintrag {
  at: number;
  /** E-Mail des Admins */
  von: string;
  stufe: Stufe;
  gueltigBis?: number;
  notiz?: string;
}

/** Offene Anfrage eines Kontos auf Standard oder Pro (docs/21 §5) */
export interface PlanAnfrage {
  stufe: 'standard' | 'pro';
  at: number; // ms
  /** freier Text des Kunden (Fragen, Bemerkungen), max 500 */
  hinweis?: string;
  /** Rechnungsadresse der Bestellung */
  rechnung?: Rechnungsadresse;
  /** Nummer der Rechnung, die mit der Bestätigung rausging — fehlt, wenn der Server keine ausstellen konnte */
  rechnungNr?: string;
}

export interface Branding {
  /** data-URL (image/png, image/svg+xml, image/jpeg), max 150 KB; fehlt = kein Logo */
  logo?: string;
  /** Akzentfarbe als #rrggbb; fehlt = Standardrot der App */
  farbe?: string;
}

export interface Konto {
  // GET /me
  sub: string;
  email?: string;
  profil: Profil;
  plan: Plan;
  branding: Branding;
  /** Betreiber-Konto (E-Mail in `AIRMAIL_ADMINS`) — zeigt den Menüpunkt „Admin" */
  admin?: true;
  /** offene Anfrage des Kontos (nach Freischaltung gelöscht) */
  anfrage?: PlanAnfrage;
  /** nur in der Antwort auf die Bestellung: `gebremst` = gespeichert, aber heute schon zu viele Bestellmails */
  mail?: 'gesendet' | 'gebremst';
}

/** Eine Zeile der Admin-Seite (GET /admin/konten) */
export interface AdminKonto {
  sub: string;
  email?: string;
  profil: Profil;
  plan: Plan; // inkl. effektiv/limits/features
  anfrage?: PlanAnfrage;
  /** Turniere in der Cloud */
  turniere: number;
  /** letzter Kontakt mit dem Server (ms) — `GET /me` setzt es */
  zuletzt?: number;
}

/** Ein Konto samt seiner Cloud-Turniere (GET /admin/konten/{sub}) */
export interface AdminKontoDetail extends AdminKonto {
  tournaments?: { id: string; name: string; date?: string; seq: number }[];
}

/** Merkmale, die ein Gate oder ein 402 vom Server nennen kann */
export type Feature = keyof Plan['features'] | 'helfer' | 'mitorganisatoren';

/** Preise (docs/21 §1) — stehen in Marke, Tabelle und Hinweisen */
export const PREIS = { standard: 99, pro: 199 } as const;

/** Was ohne Konto gilt: nichts vom Server, alles Lokale bleibt */
export const FREI: Plan = {
  stufe: 'frei',
  seit: 0,
  effektiv: 'frei',
  limits: { helfer: 0, mitorganisatoren: 0 },
  features: { branding: false, saison: false, excel: false, etiketten: false, cloud: false },
};

// ---------------------------------------------------------------------------
// Cache und Store
// ---------------------------------------------------------------------------

const CACHE_KEY = 'airmail.konto';
/** Alle 15 Minuten nachsehen — Freischaltung per Skript soll ohne Neuanmeldung ankommen */
export const LADE_INTERVALL = 15 * 60_000;

/**
 * Cache lesen, aber nur für das angemeldete Konto: Ein zweites Konto im selben
 * Browser darf nicht die Stufe des ersten erben. Kaputter Inhalt zählt als leer.
 */
export function cacheLesen(raw: string | null, sub: string | undefined): Konto | null {
  if (!raw || !sub) return null;
  try {
    const k = JSON.parse(raw) as Partial<Konto> | null;
    if (!k || k.sub !== sub || !k.plan || !k.plan.features || !k.plan.limits) return null;
    return { ...k, sub: k.sub, profil: k.profil ?? {}, plan: k.plan, branding: k.branding ?? {} };
  } catch {
    return null;
  }
}

let konto: Konto | null = null;
let laden = false;
let version = 0;
const listeners = new Set<() => void>();
function emit(): void {
  version++;
  for (const fn of listeners) fn();
}
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

function setKonto(k: Konto | null): void {
  konto = k;
  try {
    if (k) localStorage.setItem(CACHE_KEY, JSON.stringify(k));
    else localStorage.removeItem(CACHE_KEY);
  } catch {
    /* privater Modus o. Ä. — dann gilt es eben nur bis zum Neuladen */
  }
  emit();
}

/** Beim Abmelden: Cache weg — die nächste Person am Gerät fängt bei `frei` an */
export function verwerfeKonto(): void {
  setKonto(null);
}

/** Stand ohne Netz: Cache des angemeldeten Kontos */
export function kontoAusCache(): Konto | null {
  try {
    return cacheLesen(localStorage.getItem(CACHE_KEY), currentUser()?.sub);
  } catch {
    return null;
  }
}

/**
 * `GET /me` — nach dem Login und alle 15 Minuten. Legt beim ersten Aufruf
 * serverseitig die Testphase an. Offline oder bei Netzfehlern bleibt der
 * Cache stehen; ein Konto verliert seine Stufe nie, nur weil das WLAN in der
 * Halle wackelt.
 */
export async function ladeKonto(): Promise<Konto | null> {
  const user = currentUser();
  if (!user || !DEFAULT_API) return null;
  if (!konto || konto.sub !== user.sub) {
    konto = kontoAusCache();
    emit();
  }
  if (typeof navigator !== 'undefined' && !navigator.onLine) return konto;
  laden = true;
  emit();
  try {
    const k = await apiCall<Konto>(DEFAULT_API, undefined, 'GET', `/me?lang=${getLang()}`);
    // Antwort gehört zum inzwischen vielleicht abgemeldeten Konto — dann nicht mehr übernehmen
    if (currentUser()?.sub === k.sub) setKonto(k);
    return k;
  } catch {
    return konto;
  } finally {
    laden = false;
    emit();
  }
}

export async function speichereProfil(profil: Profil): Promise<Konto> {
  const k = await apiCall<Konto>(DEFAULT_API, undefined, 'PUT', '/me/profile', profil);
  setKonto(k);
  return k;
}
export async function speichereBranding(b: Branding): Promise<Konto> {
  const k = await apiCall<Konto>(DEFAULT_API, undefined, 'PUT', '/me/branding', b);
  setKonto(k);
  return k;
}
export async function loescheBranding(): Promise<Konto> {
  const k = await apiCall<Konto>(DEFAULT_API, undefined, 'DELETE', '/me/branding');
  setKonto(k);
  return k;
}

// --- Anfragen (docs/21 §5) ---------------------------------------------------------

/** Stufe bestellen — ersetzt eine ältere Anfrage; der Server stellt die Rechnung aus und mailt sie (docs/notes/rechnung.md) */
export async function sendeAnfrage(stufe: 'standard' | 'pro', rechnung: Rechnungsadresse, hinweis?: string): Promise<Konto> {
  const text = hinweis?.trim().slice(0, 500);
  const adresse: Rechnungsadresse = { firma: rechnung.firma.trim(), ort: rechnung.ort.trim(), land: (rechnung.land || 'DE').trim().toUpperCase() };
  for (const k of ['strasse', 'plz', 'ustId'] as const) {
    const v = rechnung[k]?.trim();
    if (v) adresse[k] = v;
  }
  const k = await apiCall<Konto>(DEFAULT_API, undefined, 'POST', '/me/plan/anfrage', { stufe, rechnung: adresse, appSprache: getLang(), ...(text ? { hinweis: text } : {}) });
  setKonto(k);
  return k;
}

/** Vorbelegung des Rechnungsformulars: die gemerkte Adresse, sonst Verein/Name und Ort aus dem Profil */
export function rechnungsadresseVorgabe(profil: Profil | undefined): Rechnungsadresse {
  if (profil?.rechnungsadresse) return { ...profil.rechnungsadresse };
  return { firma: profil?.verein ?? profil?.name ?? '', ort: profil?.ort ?? '', land: 'DE' };
}
export async function ziehAnfrageZurueck(): Promise<Konto> {
  const k = await apiCall<Konto>(DEFAULT_API, undefined, 'DELETE', '/me/plan/anfrage');
  setKonto(k);
  return k;
}

// --- Admin (docs/21 §5) — der Server antwortet Nicht-Admins mit 403 ----------------------

export async function adminKonten(): Promise<AdminKonto[]> {
  const r = await apiCall<AdminKonto[] | { konten: AdminKonto[] }>(DEFAULT_API, undefined, 'GET', '/admin/konten');
  // Vertrag: `AdminKonto[]` — ein umhüllendes Objekt wird trotzdem verstanden
  return Array.isArray(r) ? r : (r.konten ?? []);
}
export function adminKonto(sub: string): Promise<AdminKontoDetail> {
  return apiCall<AdminKontoDetail>(DEFAULT_API, undefined, 'GET', `/admin/konten/${encodeURIComponent(sub)}`);
}
export function adminPlanSetzen(sub: string, body: { stufe: Stufe; gueltigBis?: number; notiz?: string }): Promise<AdminKonto> {
  const notiz = body.notiz?.trim();
  return apiCall<AdminKonto>(DEFAULT_API, undefined, 'PUT', `/admin/konten/${encodeURIComponent(sub)}/plan`, {
    stufe: body.stufe,
    ...(body.gueltigBis ? { gueltigBis: body.gueltigBis } : {}),
    ...(notiz ? { notiz } : {}),
  });
}

// --- Rechnungen (docs/notes/rechnung.md) — Admin ------------------------------------------

export function adminRechnungen(): Promise<Rechnung[]> {
  return apiCall<Rechnung[]>(DEFAULT_API, undefined, 'GET', '/admin/rechnungen');
}
function rechnungAktion(nummer: string, aktion: 'bezahlt' | 'senden' | 'storno', notiz?: string): Promise<Rechnung> {
  const n = notiz?.trim();
  return apiCall<Rechnung>(DEFAULT_API, undefined, 'POST', `/admin/rechnungen/${encodeURIComponent(nummer)}/${aktion}`, n ? { notiz: n } : {});
}
/** Geld ist da: Rechnung bezahlt, Stufe für 12 Monate frei, Mail an den Kunden */
export const adminRechnungBezahlt = (nummer: string, notiz?: string) => rechnungAktion(nummer, 'bezahlt', notiz);
/** Rechnung noch einmal mailen (PDF neu aus dem Datensatz) */
export const adminRechnungSenden = (nummer: string) => rechnungAktion(nummer, 'senden');
export const adminRechnungStorno = (nummer: string, notiz?: string) => rechnungAktion(nummer, 'storno', notiz);

/**
 * Vorgabe für „gültig bis" beim Freischalten: ein Jahr ab heute, als
 * `YYYY-MM-DD` für `<input type=date>`. Der 29. Februar landet auf dem
 * 1. März — das macht `Date` von selbst, und ein Tag mehr stört niemanden.
 */
export function ablaufVorgabe(now: number): string {
  const d = new Date(now);
  d.setFullYear(d.getFullYear() + 1);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
/** `YYYY-MM-DD` → Ende dieses Tages (23:59:59 UTC) in ms; leer/kaputt → undefined */
export function ablaufMs(datum: string): number | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datum)) return undefined;
  const ms = Date.parse(`${datum}T23:59:59Z`);
  return Number.isNaN(ms) ? undefined : ms;
}

export interface PlanSicht {
  konto: Konto | null;
  effektiv: Stufe;
  features: Plan['features'];
  limits: Plan['limits'];
  laden: boolean;
}

/** Ohne Login (oder ohne Konto-Antwort) gilt `frei` mit allem auf `false` bzw. 0 */
export function ableitung(k: Konto | null, angemeldet: boolean): Omit<PlanSicht, 'laden'> {
  if (!angemeldet || !k) return { konto: angemeldet ? k : null, effektiv: FREI.effektiv, features: FREI.features, limits: FREI.limits };
  return { konto: k, effektiv: k.plan.effektiv, features: k.plan.features, limits: k.plan.limits };
}

export function usePlan(): PlanSicht {
  useSyncExternalStore(subscribe, () => version);
  const user = currentUser();
  // Ein Konto-Stand zählt nur für das Konto, das gerade angemeldet ist
  return { ...ableitung(konto && konto.sub === user?.sub ? konto : null, !!user), laden };
}

// ---------------------------------------------------------------------------
// Branding
// ---------------------------------------------------------------------------

export const LOGO_MAX_BYTES = 150 * 1024;
export const LOGO_TYPEN = ['image/png', 'image/svg+xml', 'image/jpeg'] as const;

/**
 * Logo vor dem Senden prüfen — der Server lehnt sonst mit 400 ab, und „Bad
 * Request" hilft niemandem. Gemessen wird die **data-URL selbst**, denn genau
 * die geht über die Leitung und liegt in der Datenbank; ein 120-KB-PNG ist als
 * Base64 schon zu groß. Liefert den deutschen Fehlertext (Wörterbuch-Schlüssel)
 * oder `null`, wenn alles passt.
 */
export function pruefeLogo(dataUrl: string): string | null {
  const m = /^data:([^;,]+)[;,]/.exec(dataUrl);
  if (!m || !(LOGO_TYPEN as readonly string[]).includes(m[1] ?? '')) return 'Bitte ein PNG, SVG oder JPEG wählen.';
  if (dataUrl.length > LOGO_MAX_BYTES) return 'Das Logo ist zu groß: {kb} KB, erlaubt sind 150 KB. Bild verkleinern oder als SVG speichern.';
  return null;
}
/** Größe einer data-URL in ganzen KB — für die Meldung */
export function logoKb(dataUrl: string): number {
  return Math.ceil(dataUrl.length / 1024);
}

// ---------------------------------------------------------------------------
// Texte für Marke und Hinweise
// ---------------------------------------------------------------------------

/** Wie die Stufe heißt — Wörterbuch-Schlüssel */
export function stufenName(s: Stufe): string {
  return { frei: 'Frei', standard: 'Standard', pro: 'Pro' }[s];
}

/** Noch n Tage bis `gueltigBis`, nie negativ; ein angebrochener Tag zählt */
export function restTage(gueltigBis: number | undefined, now: number): number {
  if (!gueltigBis) return 0;
  return Math.max(0, Math.ceil((gueltigBis - now) / 86_400_000));
}

type T = (de: string, vars?: Record<string, string | number>) => string;

/**
 * Text der Stufen-Marke in der Kopfzeile: „Pro · Test, 41 Tage",
 * „Standard bis 12.09.2027", „Frei". Bei `frei` steht kein Datum — es gibt keins.
 */
export function markenText(plan: Plan | null, now: number, t: T, datum: (ms: number) => string): string {
  if (!plan || plan.effektiv === 'frei') return t('Frei');
  const name = t(stufenName(plan.effektiv));
  if (plan.test) return t('{stufe} · Test, {n} Tage', { stufe: name, n: restTage(plan.gueltigBis, now) });
  if (plan.gueltigBis) return t('{stufe} bis {datum}', { stufe: name, datum: datum(plan.gueltigBis) });
  return name;
}

/** Ab welcher Stufe es ein Merkmal gibt — für die Marke am Gate */
export function noetigeStufe(feature: Feature): 'standard' | 'pro' {
  return feature === 'cloud' ? 'standard' : 'pro';
}

/**
 * Erklärung, warum etwas gesperrt ist — dieselbe für Tooltip, Hinweiskarte und
 * den Toast nach einem 402 vom Server. Deutscher Text = Wörterbuch-Schlüssel;
 * übersetzt wird beim Rendern (`t` bzw. `tm`).
 */
export function stufenHinweis(feature: Feature): string {
  switch (feature) {
    case 'cloud':
      return 'Cloud-Funktionen gibt es ab Standard (99 €/Jahr) — Konto → Stufe';
    case 'helfer':
      return 'Standard: 3 Helfer je Turnier — mehr ab Pro (199 €/Jahr) — Konto → Stufe';
    case 'mitorganisatoren':
      return 'Standard: 1 Mitorganisator je Turnier — mehr ab Pro (199 €/Jahr) — Konto → Stufe';
    case 'saison':
      return 'Saisons gibt es ab Pro (199 €/Jahr) — Konto → Stufe';
    case 'excel':
      return 'Excel-Export gibt es ab Pro (199 €/Jahr) — Konto → Stufe';
    case 'etiketten':
      return 'Etikettendruck gibt es ab Pro (199 €/Jahr) — Konto → Stufe';
    case 'branding':
      return 'Eigenes Logo und Farbe gibt es ab Pro (199 €/Jahr) — Konto → Stufe';
  }
}

const FEATURES: readonly string[] = ['branding', 'saison', 'excel', 'etiketten', 'cloud', 'helfer', 'mitorganisatoren'];
function istFeature(x: unknown): x is Feature {
  return typeof x === 'string' && FEATURES.includes(x);
}

/**
 * Fehlertext für Meldung oder Toast: Ein 402 vom Server (`{ error, feature }`)
 * bekommt dieselbe Erklärung wie das Gate in der App — sonst stünde da nur
 * „Nur in Pro" ohne Preis und ohne Weg zum Konto. Alles andere bleibt, wie es
 * der Server oder der Browser gemeldet hat.
 */
export function fehlerText(e: unknown): string {
  if (e instanceof ApiError && e.status === 402 && istFeature(e.body.feature)) return stufenHinweis(e.body.feature);
  return (e as Error).message ?? String(e);
}

/**
 * Support-Adresse für „Standard / Pro anfragen" — kommt aus dem Build
 * (`VITE_SUPPORT_MAIL`, setzt scripts/deploy.sh aus `SUPPORT_MAIL`). Ohne sie
 * zeigt die Konto-Seite keine Mail-Zeile: Ein Beispielwert stand einmal
 * sichtbar in Produktion („diese E-Mail wird nicht funktionieren", Daniel).
 */
export const SUPPORT_MAIL: string | undefined = (import.meta.env.VITE_SUPPORT_MAIL as string | undefined)?.trim() || undefined;
